import { FC } from "react";
import { Flex } from "src/components/Flex";
import * as Styled from "./PageLayout.css";
import { LoadingContainer } from "../LoadingContainer";
import { getErrorMessage } from "src/utils/getErrorMessage";
import PageHeader from "src/components/PageHeader";

export interface PageLayoutProps {
  children: React.ReactNode;
  /** What a reader can DO with the page's subject. */
  extra?: React.ReactNode;
  title: React.ReactNode;
  /** One sentence under the title: what the subject IS. */
  lead?: React.ReactNode;
  /** Where back goes, when there is a back. */
  onBack?: () => void;
  backLabel?: string;
  isLoading?: boolean;
  isError?: boolean;
  error?: any;
  /**
   * What to draw while the page is fetching.
   *
   * A page that supplies one gets its own shape back instead of a spinner
   * on an empty screen — see src/components/Skeleton. Pages that do not
   * keep the spinner.
   */
  skeleton?: React.ReactNode;
}

export const PageLayout: FC<PageLayoutProps> = ({
  children,
  extra,
  title,
  lead,
  onBack,
  backLabel,
  isLoading,
  isError,
  error,
  skeleton,
}) => {
  let Loader;
  if (isError || (isLoading && !skeleton))
    Loader = (
      <LoadingContainer
        isLoading={isLoading}
        isError={isError}
        errorMessage={getErrorMessage(error)}
      />
    );

  /* No card around the page. Its content is cards, and a white slab under a
     white card is the same box drawn twice — every hairline inside it then
     reads as dividing a surface rather than bounding an object. The card
     also carried an empty antd Title: this page passes title="", so the
     slab opened with a level-2 heading containing nothing. */
  /**
   * The page's subject, and what to do with it.
   *
   * This is new only in the sense that it now RENDERS. The component has
   * taken `title` and `extra` since it was written and drew neither: the
   * goal plan page passed the smartfolio's name and its Invest, Edit and
   * Advanced Metrics buttons and all of it was dropped on the floor, so the
   * page opened on a bare paragraph of description with no heading above it
   * and no way to act on what it described. The other two callers pass
   * `title=""` and bring their own header, so nothing appears for them and
   * nothing changes.
   */
  const header = title ? (
    <PageHeader
      name={title as string}
      lead={lead}
      actions={extra}
      onBack={onBack}
      backLabel={backLabel}
    />
  ) : null;

  return (
    <Styled.Wrapper>
      {Loader ?? (
        /* Full width, explicitly. The Flex helper defaults to width: auto,
           and this column sits inside a ROW, where a child's width is its
           content's unless it is told otherwise — so the page stopped
           wherever its widest card happened to stop and left the rest of the
           screen empty. */
        /* The skeleton sits in the same column as the page it stands in
           for, so its pieces are spaced the way the real ones will be. */
        <Flex flexDirection="column" gap="1rem" width="100%">
          {header}
          {isLoading && skeleton ? skeleton : children}
        </Flex>
      )}
    </Styled.Wrapper>
  );
};
